import { useState } from 'react';
import { ArrowLeft, ArrowRight, Camera, ImagePlus, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { normalize } from '../../data/catalog';
import { normalizeImageForAI } from '../../utils/siteConfig';
import { uploadImage } from '../../utils/photoStatus';
import { useQueue } from '../../context/QueueContext';

const PATTERNS = ['Liso', 'Estampado', 'Xadrez', 'Listrado', 'Jacquard'];
const MAX_FOTOS = 8;

const normTokens = (s) => normalize(s || '').split(/\s+/).filter((t) => t.length > 2);

// Comparação por METADADOS (nome, padrão, descrição, código) das variantes já
// cadastradas — nunca bloqueia, apenas sinaliza. Mesmo nome/mesmas cores NÃO
// significam mesma variante: a decisão final é sempre do admin.
function classifyVariantMatch(name, pattern, existingVariants) {
  const norm = normalize(name || '');
  if (!norm) return { status: 'nova', existing: null };
  for (const ev of existingVariants || []) {
    if (normalize(ev.name || '') === norm) return { status: 'duplicidade', existing: ev };
  }
  const toks = normTokens(name);
  if (!toks.length) return { status: 'nova', existing: null };
  let best = null;
  for (const ev of existingVariants || []) {
    const etoks = normTokens(ev.name);
    if (!etoks.length) continue;
    const shared = etoks.filter((t) => toks.includes(t)).length;
    const cobertura = shared / Math.max(toks.length, etoks.length);
    const mesmoPadrao = pattern && ev.pattern && pattern === ev.pattern && pattern !== 'Liso';
    if (shared > 0 && (cobertura >= 0.5 || mesmoPadrao) && (!best || shared > best.shared)) {
      best = { ev, shared };
    }
  }
  return best ? { status: 'semelhante', existing: best.ev } : { status: 'nova', existing: null };
}

// Análise de variantes por MÚLTIPLAS FOTOS — implementação única compartilhada entre
// o Cadastro em Lote (AdminBatchProduct) e a edição de produto (AdminEditProduct).
// Fluxo: fotos do conjunto (1–8, normalizadas p/ celular) → análise conjunta na IA
// (cor + padrão + assinatura visual, dedupe entre fotos) → revisão humana. Nada é
// gravado aqui: onConfirm entrega apenas as variantes selecionadas e revisadas.
export function VariantAIAnalysis({
  testidPrefix,
  productName,
  notes = '',
  existingVariants = [],
  confirmLabel = 'CONTINUAR',
  confirmTestid,
  confirmDisabled = false,
  onConfirm,
  onBack,
  backLabel = 'VOLTAR',
  fotoTitle = 'Fotos do conjunto',
  reviewTitle = 'Variantes identificadas',
}) {
  const { user } = useQueue();
  const tp = testidPrefix;
  const [stage, setStage] = useState('fotos');
  const [photos, setPhotos] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');
  const [viewer, setViewer] = useState(null);
  const [rows, setRows] = useState([]);
  const [confirmError, setConfirmError] = useState('');

  const patchRow = (key, patch) => setRows((l) => l.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const activeRows = rows.filter((r) => r.checked && r.dest === 'nova');

  // Fotos do conjunto (câmera sucessiva / galeria múltipla) — normalizadas na entrada
  // (EXIF, JPEG, tamanho). Material de análise: NUNCA viram foto pública de variante.
  const addPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setAnalyzeError('');
    for (const file of files) {
      try {
        const dataUrl = await normalizeImageForAI(file);
        setPhotos((l) => (l.length >= MAX_FOTOS ? l : [...l, { id: `p${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, dataUrl }]));
      } catch {
        setAnalyzeError('Uma das fotos tem formato incompatível — use JPEG, PNG ou WEBP.');
      }
    }
  };

  // Análise REAL (visão): todas as fotos vão juntas; a IA entende serem registros do
  // mesmo produto e sugere variantes deduplicadas (mesma variante em 2 fotos = uma
  // sugestão com FOTOS 1+2; mesma cor visualmente diferente = sugestões separadas).
  const analyze = async () => {
    setAnalyzing(true);
    setAnalyzeError('');
    try {
      // Upload individual de cada foto já normalizada (arquivos pequenos) e a análise
      // recebe apenas URLs — evita corpo JSON gigante (413 em produção). As fotos vão
      // ao escopo temporário "analise" e o backend as remove do storage após ler.
      const refs = [];
      for (const p of photos) {
        refs.push(await uploadImage(p.dataUrl, 'analise'));
      }
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/analyze-variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Role': user?.role || '' },
        body: JSON.stringify({ photos: refs, productName, notes }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || `IA ${res.status}`);
      const list = Array.isArray(d.variants) ? d.variants : [];
      setRows(
        list.map((v, i) => ({
          key: `v${i + 1}`,
          name: v.name || '',
          pattern: PATTERNS.includes(v.pattern) ? v.pattern : 'Liso',
          code: '',
          photoIndexes: Array.isArray(v.photoIndexes) ? v.photoIndexes : [],
          confidence: ['alta', 'media', 'baixa'].includes(v.confidence) ? v.confidence : 'media',
          visualNote: v.visualNote || '',
          checked: true,
          manual: false,
          dest: 'nova',
        }))
      );
      setConfirmError('');
      setStage('revisao');
    } catch (err) {
      setAnalyzeError(err.message || 'Não foi possível analisar as fotos agora.');
    } finally {
      setAnalyzing(false);
    }
  };

  const addManual = () =>
    setRows((l) => [
      ...l,
      { key: `m${Date.now()}`, name: '', pattern: 'Liso', code: '', photoIndexes: [], confidence: 'alta', visualNote: '', checked: true, manual: true, dest: 'nova' },
    ]);

  // Confirmação com última proteção: nomes finais iguais entre sugestões novas
  // precisam ser renomeados antes de qualquer gravação. Isso NÃO significa que as
  // variantes sejam iguais — apenas que nomes finais distintos são obrigatórios.
  const confirm = () => {
    const selected = rows.filter((r) => r.checked && r.dest === 'nova');
    const byName = new Map();
    selected.forEach((r) => {
      const norm = normalize(r.name || '');
      if (!norm) return;
      byName.set(norm, [...(byName.get(norm) || []), r.key]);
    });
    const siblingKeys = new Set([...byName.values()].filter((keys) => keys.length > 1).flat());
    if (siblingKeys.size) {
      setRows(rows.map((r) => ({ ...r, nameConflict: siblingKeys.has(r.key), siblingConflict: siblingKeys.has(r.key) })));
      setConfirmError('Duas sugestões selecionadas têm o mesmo nome. Renomeie ou descarte uma delas.');
      return;
    }
    let conflict = false;
    const flagged = rows.map((r) => {
      const c = r.checked && r.dest === 'nova' && (existingVariants || []).some((ev) => normalize(ev.name || '') === normalize(r.name));
      if (c) conflict = true;
      return { ...r, nameConflict: c, siblingConflict: false };
    });
    if (conflict) {
      setRows(flagged);
      setConfirmError('Já existe uma variante com este nome. Altere o nome da nova ou escolha USAR EXISTENTE.');
      return;
    }
    if (!selected.length) {
      setConfirmError('Nenhuma variante selecionada.');
      return;
    }
    if (selected.some((r) => !r.name.trim())) {
      setConfirmError('Toda variante selecionada precisa de um nome.');
      return;
    }
    setConfirmError('');
    onConfirm(selected.map((r) => ({ key: r.key, name: r.name.trim(), pattern: r.pattern, code: r.code, photoIndexes: r.photoIndexes, confidence: r.confidence, visualNote: r.visualNote, manual: !!r.manual })));
  };

  return (
    <div data-testid={`${tp}-ai-analysis`}>
      {stage === 'fotos' && (
        <section className="mt-6 rounded-2xl border border-navy/10 bg-white p-5" data-testid={`${tp}-step-foto`}>
          <h3 className="font-serif text-lg text-navy">{fotoTitle}</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Fotografe os rolos/cores/variantes do mesmo tecido — <strong>várias fotos, se uma só não mostrar tudo</strong>.
            As fotos servem apenas para a análise da IA (nunca viram foto pública de variante); as fotos definitivas de
            cada variante entram depois, sob seu controle.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <button type="button" onClick={() => document.getElementById(`${tp}-photo-camera`)?.click()} data-testid={`${tp}-photo-camera-button`} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-navy text-xs font-bold text-white hover:bg-navy-hover">
              <Camera className="h-4 w-4" /> TIRAR FOTO
            </button>
            <button type="button" onClick={() => document.getElementById(`${tp}-photo-gallery`)?.click()} data-testid={`${tp}-photo-gallery-button`} className="flex h-12 items-center justify-center gap-2 rounded-xl border border-navy/25 text-xs font-bold text-navy hover:bg-navy-light">
              <ImagePlus className="h-4 w-4" /> ESCOLHER DA GALERIA
            </button>
          </div>
          <input id={`${tp}-photo-camera`} data-testid={`${tp}-photo-camera-input`} type="file" accept="image/*" capture="environment" className="sr-only" onChange={addPhotos} />
          <input id={`${tp}-photo-gallery`} data-testid={`${tp}-photo-gallery-input`} type="file" accept="image/*" multiple className="sr-only" onChange={addPhotos} />
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid={`${tp}-photo-count`}>
            {photos.length === 0
              ? 'Nenhuma foto adicionada ainda — adicione de 1 a 8.'
              : `${photos.length} de 8 foto(s) adicionada(s). As fotos são otimizadas automaticamente (orientação, tamanho e formato).`}
          </p>
          {photos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid={`${tp}-photo-thumbs`}>
              {photos.map((p, i) => (
                <div key={p.id} className="relative">
                  <button type="button" onClick={() => setViewer(i)} data-testid={`${tp}-photo-thumb-${i}`} className="block w-full overflow-hidden rounded-xl border border-navy/10" title={`Ampliar foto ${i + 1}`}>
                    <img src={p.dataUrl} alt={`Foto ${i + 1} do conjunto`} className="h-24 w-full object-cover" />
                  </button>
                  <span className="absolute left-1.5 top-1.5 rounded-full bg-navy px-2 py-0.5 text-[10px] font-bold text-white">{i + 1}</span>
                  <button type="button" onClick={() => setPhotos((l) => l.filter((x) => x.id !== p.id))} aria-label={`Remover foto ${i + 1}`} data-testid={`${tp}-photo-remove-${i}`} className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[11px] font-bold text-white">
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          {analyzeError && (
            <p className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs font-medium text-amber-800" data-testid={`${tp}-analyze-error`}>
              {analyzeError}
            </p>
          )}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            {onBack && (
              <button type="button" onClick={onBack} className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light" data-testid={`${tp}-back-dados`}>
                <ArrowLeft className="h-4 w-4" /> {backLabel}
              </button>
            )}
            <button type="button" onClick={analyze} disabled={photos.length === 0 || analyzing} data-testid={`${tp}-analyze-button`} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none">
              <Sparkles className="h-4 w-4" /> {analyzing ? 'ANALISANDO FOTOS…' : 'ANALISAR VARIANTES COM IA'}
            </button>
          </div>
          {analyzeError && (
            <button type="button" onClick={() => { setRows([]); setConfirmError(''); setStage('revisao'); }} data-testid={`${tp}-skip-analysis`} className="mt-1 text-xs font-semibold text-navy underline">
              Continuar sem análise — adicionar variantes manualmente
            </button>
          )}
        </section>
      )}

      {stage === 'revisao' && (
        <section className="mt-6" data-testid={`${tp}-step-variantes`}>
          <h3 className="font-serif text-lg text-navy">{reviewTitle}</h3>
          <p className="mt-1 text-sm text-muted-foreground" data-testid={`${tp}-variants-count`}>
            {activeRows.length} variante(s) selecionada(s) de {rows.length} identificada(s). Revise nomes, padrões e códigos — exclua o que não for variante.
          </p>
          {existingVariants.length > 0 && (
            <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`${tp}-existing-count`}>
              Este produto já possui {existingVariants.length} variante(s) cadastrada(s) — sugestões parecidas serão sinalizadas, mas a decisão é sempre sua. Mesmo nome ou mesmas cores não significam mesma variante.
            </p>
          )}
          <div className="mt-4 space-y-3">
            {rows.map((v) => {
              const inactive = !v.checked || v.dest !== 'nova';
              const match = classifyVariantMatch(v.name, v.pattern, existingVariants);
              return (
                <div key={v.key} className={`rounded-2xl border p-3 ${inactive ? 'border-navy/10 bg-navy-light/30 opacity-60' : v.nameConflict ? 'border-red-400 bg-red-50/40' : v.confidence === 'baixa' ? 'border-amber-400 bg-amber-50/40' : 'border-navy/10 bg-white'}`} data-testid={`${tp}-variant-row-${v.key}`}>
                  <div className="flex gap-3">
                    <label className="flex items-start pt-1" data-testid={`${tp}-variant-check-label-${v.key}`}>
                      <input
                        type="checkbox"
                        checked={v.checked}
                        onChange={(e) => patchRow(v.key, { checked: e.target.checked, nameConflict: false })}
                        aria-label={`Incluir variante ${v.name || v.key}`}
                        data-testid={`${tp}-variant-check-${v.key}`}
                        className="h-5 w-5 accent-navy"
                      />
                    </label>
                    <div className="min-w-0 flex-1 space-y-2">
                      <input
                        value={v.name}
                        onChange={(e) => patchRow(v.key, { name: e.target.value, nameConflict: false, siblingConflict: false })}
                        placeholder="Cor / Estampa / Variante (ex.: Azul Royal)"
                        aria-label="Cor / Estampa / Variante"
                        data-testid={`${tp}-variant-name-${v.key}`}
                        className="h-11 w-full rounded-xl border border-input bg-white px-3 text-sm font-medium outline-none focus:border-navy"
                      />
                      {!v.manual && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {existingVariants.length > 0 && match.status === 'nova' && (
                            <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-bold text-emerald-800 ring-1 ring-emerald-300" data-testid={`${tp}-variant-match-${v.key}`}>
                              NOVA
                            </span>
                          )}
                          {match.status === 'semelhante' && (
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-300" data-testid={`${tp}-variant-match-${v.key}`}>
                              ⚠ POSSÍVEL VARIANTE SEMELHANTE — já cadastrada: “{match.existing.name}”
                            </span>
                          )}
                          {match.status === 'duplicidade' && (
                            <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-[10px] font-bold text-red-800 ring-1 ring-red-300" data-testid={`${tp}-variant-match-${v.key}`}>
                              ⚠ POSSÍVEL DUPLICIDADE — já cadastrada: “{match.existing.name}”
                            </span>
                          )}
                          {v.confidence === 'baixa' && (
                            <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-300" data-testid={`${tp}-variant-lowconf-${v.key}`}>
                              REVISAR SUGESTÃO
                            </span>
                          )}
                          {(v.photoIndexes || []).map((pi) => (
                            <button key={pi} type="button" onClick={() => setViewer(pi)} data-testid={`${tp}-variant-photo-${v.key}-${pi}`} className="rounded-full bg-navy/10 px-2.5 py-0.5 text-[10px] font-bold text-navy hover:bg-navy/20" title={`Ver a foto ${pi + 1} em que a variante foi identificada`}>
                              FOTO {pi + 1}
                            </button>
                          ))}
                        </div>
                      )}
                      {v.visualNote ? (
                        <p className="text-[11px] italic text-muted-foreground" data-testid={`${tp}-variant-visualnote-${v.key}`}>
                          Nota visual da IA: {v.visualNote}
                        </p>
                      ) : null}
                      {v.nameConflict && (
                        <p className="text-[11px] font-semibold text-red-700" data-testid={v.siblingConflict ? `${tp}-variant-siblingconflict-${v.key}` : `${tp}-variant-nameconflict-${v.key}`}>
                          {v.siblingConflict
                            ? 'Duas sugestões selecionadas têm o mesmo nome. Renomeie ou descarte uma delas.'
                            : 'Já existe uma variante com este nome. Altere o nome da nova ou escolha USAR EXISTENTE.'}
                        </p>
                      )}
                      {match.status !== 'nova' && (
                        <div className="flex flex-wrap gap-1.5" data-testid={`${tp}-variant-actions-${v.key}`}>
                          <button type="button" onClick={() => patchRow(v.key, { dest: 'existente', nameConflict: false })} data-testid={`${tp}-variant-use-existing-${v.key}`} className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${v.dest === 'existente' ? 'bg-navy text-white' : 'border border-navy/25 text-navy hover:bg-navy-light'}`}>
                            USAR EXISTENTE
                          </button>
                          <button type="button" onClick={() => patchRow(v.key, { dest: 'nova' })} data-testid={`${tp}-variant-new-${v.key}`} className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${v.dest === 'nova' ? 'bg-navy text-white' : 'border border-navy/25 text-navy hover:bg-navy-light'}`}>
                            CADASTRAR COMO NOVA
                          </button>
                          <button type="button" onClick={() => patchRow(v.key, { dest: 'descartar', nameConflict: false })} data-testid={`${tp}-variant-discard-${v.key}`} className={`rounded-full px-3 py-1.5 text-[10px] font-bold ${v.dest === 'descartar' ? 'bg-red-600 text-white' : 'border border-red-300 text-red-700 hover:bg-red-50'}`}>
                            DESCARTAR
                          </button>
                        </div>
                      )}
                      {v.dest === 'existente' && (
                        <p className="text-[11px] font-semibold text-navy" data-testid={`${tp}-variant-useexisting-note-${v.key}`}>Nenhuma variante nova será criada — a existente será mantida.</p>
                      )}
                      {v.dest === 'descartar' && (
                        <p className="text-[11px] font-semibold text-red-700" data-testid={`${tp}-variant-discard-note-${v.key}`}>Sugestão descartada — não será cadastrada.</p>
                      )}
                      <div className="grid grid-cols-2 gap-2">
                        <select value={v.pattern} onChange={(e) => patchRow(v.key, { pattern: e.target.value })} aria-label="Cor/padrão" data-testid={`${tp}-variant-pattern-${v.key}`} className="h-11 rounded-xl border border-input bg-white px-3 text-sm outline-none focus:border-navy">
                          {PATTERNS.map((p) => (
                            <option key={p}>{p}</option>
                          ))}
                        </select>
                        <input
                          value={v.code}
                          onChange={(e) => patchRow(v.key, { code: e.target.value })}
                          placeholder="Código interno (opcional)"
                          aria-label="Código interno"
                          data-testid={`${tp}-variant-code-${v.key}`}
                          className="h-11 rounded-xl border border-input bg-white px-3 text-sm outline-none focus:border-navy"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {inactive ? (
                      <button type="button" onClick={() => patchRow(v.key, { checked: true, dest: 'nova', nameConflict: false })} data-testid={`${tp}-variant-restore-${v.key}`} className="flex h-10 items-center rounded-xl border border-emerald-500/50 px-4 text-xs font-semibold text-emerald-700 hover:bg-emerald-50">
                        RESTAURAR
                      </button>
                    ) : (
                      <>
                        <button type="button" onClick={() => patchRow(v.key, { checked: false })} data-testid={`${tp}-variant-exclude-${v.key}`} className="flex h-10 items-center gap-2 rounded-xl border border-red-300 px-4 text-xs font-semibold text-red-700 hover:bg-red-50">
                          <Trash2 className="h-4 w-4" /> EXCLUIR
                        </button>
                        <button type="button" onClick={() => patchRow(v.key, { dest: 'descartar' })} data-testid={`${tp}-variant-notvariant-${v.key}`} className="flex h-10 items-center gap-2 rounded-xl border border-amber-400/60 px-4 text-xs font-semibold text-amber-700 hover:bg-amber-50">
                          <X className="h-4 w-4" /> NÃO É VARIANTE
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={addManual} data-testid={`${tp}-variant-add`} className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-navy/25 px-4 py-4 text-sm font-semibold text-navy hover:bg-navy-light">
            <Plus className="h-4 w-4" /> ADICIONAR VARIANTE NÃO DETECTADA
          </button>
          {confirmError && (
            <p className="mt-3 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-xs font-medium text-red-700" data-testid={`${tp}-confirm-error`}>
              {confirmError}
            </p>
          )}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row">
            <button type="button" onClick={() => setStage('fotos')} className="flex items-center justify-center gap-2 rounded-full border border-navy/20 px-6 py-3.5 text-sm font-semibold text-navy hover:bg-navy-light" data-testid={`${tp}-back-foto`}>
              <ArrowLeft className="h-4 w-4" /> VOLTAR ÀS FOTOS
            </button>
            <button type="button" onClick={confirm} disabled={confirmDisabled || activeRows.length === 0} data-testid={confirmTestid || `${tp}-next-variantes`} className="flex flex-1 items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-semibold text-white hover:bg-navy-hover disabled:cursor-not-allowed disabled:opacity-40 sm:flex-none">
              {confirmLabel} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </section>
      )}

      {viewer !== null && photos[viewer] && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" data-testid={`${tp}-photo-viewer`} onClick={() => setViewer(null)}>
          <div className="max-w-lg">
            <img src={photos[viewer].dataUrl} alt={`Foto ${viewer + 1} do conjunto`} className="max-h-[75vh] w-full rounded-2xl object-contain" />
            <p className="mt-3 text-center text-sm font-semibold text-white">Foto {viewer + 1} de {photos.length} — toque fora para fechar</p>
          </div>
        </div>
      )}
    </div>
  );
}
